import { useCallback, useEffect, useRef, useState } from 'react';

export const useScroller = (scrollBy: number) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (!mounted) setMounted(true);
  }, [mounted]);

  const scrollContainer = useRef<HTMLDivElement>(null);
  const { scrollWidth = 0, clientWidth = 0 } = scrollContainer.current || {};

  const [scrollDistance, setScrollDistance] = useState(scrollContainer.current?.scrollLeft || 0);
  const hasOverflow = scrollWidth > clientWidth;
  const isAtLeft = scrollDistance === 0;
  const isAtRight = Math.abs(scrollDistance + clientWidth - scrollWidth) < 1;
  const canScrollLeft = hasOverflow && !isAtLeft;
  const canScrollRight = hasOverflow && !isAtRight;

  const scroll = (distance: number) => {
    if (!scrollContainer.current) return;
    const newScrollDistance = scrollDistance + distance;
    scrollContainer.current.scroll({ left: newScrollDistance, behavior: 'smooth' });
    setScrollDistance(newScrollDistance);
  };

  const scrollLeft = () => canScrollLeft && scroll(-1 * scrollBy);
  const scrollRight = () => canScrollRight && scroll(scrollBy);
  const onScroll = (e: React.UIEvent<HTMLElement>) => setScrollDistance(e.currentTarget.scrollLeft);
  const reset = useCallback(() => {
    if (!scrollContainer.current) return;
    scrollContainer.current.scroll({ left: 0 });
    setScrollDistance(0);
  }, []);

  return {
    scrollRef: scrollContainer,
    canScrollLeft,
    canScrollRight,
    scrollLeft,
    scrollRight,
    onScroll,
    reset,
  };
};
